/**
 * VerificationModal - UI for device verification flows
 *
 * Handles both initiator and responder flows:
 * - Initiator: Starts verification using VerificationFlowController
 * - Responder: Accepts incoming verification request
 * - Uses reusable EmojiVerificationScreen component
 * - Supports both device and user verification
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import verificationFlowController, { VerificationState } from '../../crypto/verification/VerificationFlowController';
import useVerificationFlow, { VerificationFlowState } from '../../hooks/useVerificationFlow';
import EmojiVerificationScreen from './EmojiVerificationScreen';
import logger from '../../utils/logger';
import './VerificationModal.css';

/**
 * VerificationModal Component
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback when modal closes
 * @param {Function} onComplete - Callback when verification completes (success boolean)
 * @param {Object} incomingRequest - Incoming verification request (responder flow)
 * @param {string} verificationType - Type of verification ('device' or 'user')
 * @param {string} displayName - Display name for context (device name or user name)
 */
const VerificationModal = ({ 
  isOpen, 
  onClose, 
  onComplete,
  incomingRequest = null,
  verificationType = 'device',
  displayName = null,
}) => {
  // Determine if this is initiator or responder flow
  const isResponder = !!incomingRequest;

  // State for initiator flow (using VerificationFlowController)
  const [verificationState, setVerificationState] = useState(VerificationState.IDLE);
  const [sasData, setSasData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initiatorError, setInitiatorError] = useState(null);
  const [confirmingMatch, setConfirmingMatch] = useState(false);

  // State for responder flow (using useVerificationFlow hook)
  const {
    state: responderState,
    emojis: responderEmojis,
    error: responderError,
    acceptRequest,
    rejectRequest,
    confirmMatch,
    rejectMatch,
    cancel: cancelResponder,
  } = useVerificationFlow(incomingRequest, verificationType);

  // Unified state and data based on flow type
  const currentState = isResponder ? responderState : verificationState;
  const currentEmojis = useMemo(() => {
    const emojis = isResponder ? responderEmojis : sasData?.emoji;
    return emojis;
  }, [isResponder, responderEmojis, sasData]);
  const currentError = isResponder ? responderError : initiatorError;

  // Show accept/reject screen for incoming requests
  // Only show if request phase is still 'requested' (not yet accepted)
  const [showAcceptScreen, setShowAcceptScreen] = useState(
    isResponder && incomingRequest?.phase === 2,  // phase 2 = requested
  );

  const unsubscribeRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasStartedVerificationRef = useRef(false);  // Prevent restart during completion
  
  // Store completion handlers in refs to avoid stale closures
  const completionHandlersRef = useRef({
    complete: null,
    cancelled: null,
  });

  // Handle verification complete
  const handleVerificationComplete = useCallback(() => {
    logger.info('VerificationModal: Verification complete');
    // Close immediately - no delay - to prevent restart loop
    if (!isMountedRef.current) return;
    onComplete?.(true);
    onClose();
  }, [onComplete, onClose]);

  // Handle verification cancelled
  const handleVerificationCancelled = useCallback(() => {
    logger.info('VerificationModal: Verification cancelled');
    hasStartedVerificationRef.current = false;  // Allow retry after cancel
    onComplete?.(false);
    onClose();
  }, [onComplete, onClose]);

  // Update completion handlers ref when callbacks change
  completionHandlersRef.current = {
    complete: handleVerificationComplete,
    cancelled: handleVerificationCancelled,
  };

  // Start verification when modal opens (initiator flow only)
  // For responder flow, check if request was already accepted
  useEffect(() => {
    if (!isOpen) return;

    if (!isResponder && verificationState === VerificationState.IDLE) {
      // Initiator flow: start verification
      logger.info('Opening verification modal as initiator');
      startVerification();
    } else if (isResponder && incomingRequest) {
      // Responder flow: check if already accepted (phase > 2)
      logger.info('Opening verification modal as responder');
      if (incomingRequest.phase > 2) {
        setShowAcceptScreen(false);
      }
    }

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isOpen, isResponder, incomingRequest]);

  // Reset guard when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasStartedVerificationRef.current = false;
    }
  }, [isOpen]);

  // Start verification flow
  const startVerification = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    // Prevent multiple verification attempts in same session
    if (hasStartedVerificationRef.current) {
      return;
    }
    hasStartedVerificationRef.current = true;  // Mark as started
    // Don't set isLoading here - the REQUESTING state already shows a spinner
    // isLoading is only for the accept button in responder flow
    setInitiatorError(null);

    // Subscribe to state changes
    unsubscribeRef.current = verificationFlowController.onStateChange((newState) => {
      if (!isMountedRef.current) return;
      
      setVerificationState(newState);

      const currentData = verificationFlowController.getCurrentState();

      // CRITICAL: Only set sasData when emojis are actually present
      // This prevents race condition where sasData exists but emoji array is not yet populated
      if (currentData.sasData?.emoji) {
        setSasData(currentData.sasData);
        // No need to clear isLoading here - we don't set it in initiator flow
      }

      // Handle completion states using ref to avoid stale closures
      if (newState === VerificationState.COMPLETE) {
        completionHandlersRef.current.complete?.();
      } else if (newState === VerificationState.CANCELLED) {
        completionHandlersRef.current.cancelled?.();
      } else if (newState === VerificationState.ERROR) {
        setInitiatorError('Verification failed. Please try again.');
      }
    });

    // CRITICAL: Check current state immediately after subscribing
    // to catch any state changes that happened during async setup
    const currentState = verificationFlowController.getCurrentState();
    if (currentState.state !== VerificationState.IDLE && currentState.state !== VerificationState.REQUESTING) {
      setVerificationState(currentState.state);
      // Only set sasData if emojis are present
      if (currentState.sasData?.emoji) {
        setSasData(currentState.sasData);
        // No need to clear isLoading - we don't set it in initiator flow
      }
    }

    try {
      const result = await verificationFlowController.startFallbackForSelf();
      if (!isMountedRef.current) return;
      
      logger.debug('VerificationModal: Verification result:', result);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Failed to start verification:', err);
      setInitiatorError('Failed to start verification');
    }
    // No finally block needed - isLoading not used in initiator flow
  }, []);

  // Handle accept for incoming request (responder flow)
  const handleAccept = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    
    try {
      const success = await acceptRequest();
      
      if (!isMountedRef.current) return;
      
      if (success) {
        setShowAcceptScreen(false);
      } else {
        // acceptRequest failed, error already set by hook
        logger.warn('VerificationModal: Failed to accept request');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Error accepting request:', err);
      // Error already set by the hook
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [acceptRequest]);

  // Handle reject for incoming request (responder flow)
  const handleReject = useCallback(async () => {
    try {
      await rejectRequest('user');
      
      if (!isMountedRef.current) return;
      
      onClose();
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Error rejecting request:', err);
      // Still close the modal even if reject fails
      onClose();
    }
  }, [rejectRequest, onClose]);

  // Handle SAS confirmation
  const handleSasConfirm = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setConfirmingMatch(true);

    try {
      let success;
      if (isResponder) {
        success = await confirmMatch();
      } else {
        success = await verificationFlowController.confirmSasMatch();
      }
      
      if (!isMountedRef.current) return;
      
      if (!success && !isResponder) {
        setInitiatorError('Failed to confirm match');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Failed to confirm SAS:', err);
      if (!isResponder) {
        setInitiatorError('Failed to confirm match');
      }
    } finally {
      if (isMountedRef.current) {
        setConfirmingMatch(false);
      }
    }
  }, [isResponder, confirmMatch]);

  // Handle SAS rejection
  const handleSasReject = useCallback(async () => {
    if (isResponder) {
      await rejectMatch();
    } else {
      verificationFlowController.cancel('mismatch');
      
      if (!isMountedRef.current) return;
      
      setInitiatorError('Verification cancelled - emojis did not match');
    }
  }, [isResponder, rejectMatch]);

  // Cancel verification
  const handleCancel = useCallback(() => {
    if (isResponder) {
      cancelResponder('user');
    } else {
      verificationFlowController.cancel('user');
    }
    onClose();
  }, [isResponder, cancelResponder, onClose]);

  // Render accept/reject screen for incoming requests
  const renderAcceptScreen = () => {
    if (!isResponder) return null;

    return (
      <div className="verification-accept-screen">
        <div className="verification-accept-icon">
          <span>🔐</span>
        </div>
        <h3>New Verification Request</h3>
        <p className="verification-accept-message">
          {displayName ? (
            <>
              <strong>{displayName}</strong> wants to verify
            </>
          ) : (
            <>A {verificationType} wants to verify</>
          )}
        </p>
        <p className="verification-accept-description">
          Compare emojis to confirm the connection is secure.
        </p>
        <div className="verification-accept-actions">
          <button
            className="btn btn-secondary"
            onClick={handleReject}
            disabled={isLoading}
          >
            Reject
          </button>
          <button
            className="btn btn-success"
            onClick={handleAccept}
            disabled={isLoading}
          >
            {isLoading ? 'Accepting...' : 'Accept'}
          </button>
        </div>
      </div>
    );
  };

  // Render SAS emoji display using reusable component
  const renderSasEmojis = () => {
    if (!currentEmojis) {
      return null;
    }
    return (
      <EmojiVerificationScreen
        emojis={currentEmojis}
        onConfirm={handleSasConfirm}
        onReject={handleSasReject}
        isConfirming={confirmingMatch}
        contextText={displayName}
        verificationType={verificationType}
      />
    );
  };

  // Render content based on unified state
  const renderContent = () => {
    // Show accept/reject screen first for responder flow
    if (isResponder && showAcceptScreen) {
      return renderAcceptScreen();
    }

    // Map responder states to initiator states for unified rendering
    const mappedState = isResponder ? mapResponderState(responderState) : verificationState;

    switch (mappedState) {
      case VerificationState.IDLE:
      case VerificationState.REQUESTING:
        return (
          <div className="verification-loading">
            <div className="spinner" />
            <p>Starting verification...</p>
          </div>
        );

      case VerificationState.READY:
        return (
          <div className="verification-ready">
            <p>Waiting for the other {verificationType} to respond...</p>
            <div className="spinner" />
          </div>
        );

      case VerificationState.SHOW_SAS:
        return renderSasEmojis();

      case VerificationState.VERIFYING:
        return (
          <div className="verification-verifying">
            <div className="spinner" />
            <p>Waiting for confirmation...</p>
          </div>
        );

      case VerificationState.COMPLETE:
        return (
          <div className="verification-complete">
            <div className="success-icon">✓</div>
            <h3>Verification Complete!</h3>
            <p>
              {verificationType === 'user' 
                ? 'The user is now verified.' 
                : 'Your device is now verified.'}
            </p>
          </div>
        );

      case VerificationState.CANCELLED:
        return (
          <div className="verification-cancelled">
            <div className="warning-icon">⚠️</div>
            <h3>Verification Cancelled</h3>
            <p>The verification was cancelled.</p>
          </div>
        );

      case VerificationState.ERROR:
        return (
          <div className="verification-error">
            <div className="error-icon">❌</div>
            <h3>Verification Failed</h3>
            <p>{currentError || 'An error occurred during verification.'}</p>
            {!isResponder && (
              <button className="btn btn-primary" onClick={startVerification}>
                Try Again
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Map responder states to initiator states for unified rendering
  const mapResponderState = (state) => {
    switch (state) {
      case VerificationFlowState.IDLE:
        return VerificationState.IDLE;
      case VerificationFlowState.ACCEPTING:
        return VerificationState.REQUESTING;
      case VerificationFlowState.WAITING:
        return VerificationState.READY;
      case VerificationFlowState.SHOWING_SAS:
        return VerificationState.SHOW_SAS;
      case VerificationFlowState.VERIFYING:
        return VerificationState.VERIFYING;
      case VerificationFlowState.DONE:
        return VerificationState.COMPLETE;
      case VerificationFlowState.CANCELLED:
        return VerificationState.CANCELLED;
      case VerificationFlowState.ERROR:
        return VerificationState.ERROR;
      default:
        return VerificationState.IDLE;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="verification-modal-overlay">
      <div className="verification-modal">
        <div className="verification-modal-header">
          <h2>
            {verificationType === 'user' ? 'Verify User' : 'Verify Device'}
          </h2>
          {currentState !== VerificationState.COMPLETE && (
            <button className="verification-modal-close" onClick={handleCancel}>
              ×
            </button>
          )}
        </div>

        <div className="verification-modal-content">
          {currentError && currentState !== VerificationState.ERROR && (
            <div className="verification-error-message">
              {currentError}
            </div>
          )}

          {renderContent()}
        </div>

        {(currentState === VerificationState.COMPLETE || currentState === VerificationFlowState.DONE) ? (
          <div className="verification-modal-footer">
            <button className="btn btn-primary" onClick={onClose}>
              OK
            </button>
          </div>
        ) : currentState !== VerificationState.CANCELLED &&
          currentState !== VerificationState.ERROR &&
          currentState !== VerificationFlowState.CANCELLED && (
          <div className="verification-modal-footer">
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        )}

        {isLoading && (
          <div className="verification-modal-loading">
            <div className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

VerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  incomingRequest: PropTypes.object,
  verificationType: PropTypes.oneOf(['device', 'user']),
  displayName: PropTypes.string,
};

export default VerificationModal;